export const reviewQuestions = [
  "What should I evaluate first?",
  "How should I organize the procedure?",
  "Which variables should influence my planning?",
  "What should I review before moving forward?",
];

export const frameworkSteps = ["Evaluate", "Plan", "Prepare", "Sequence", "Review", "Learn"];

export type PainKey = "overwhelm" | "time" | "puzzle" | "passive" | "search" | "brain";

export const pains: { key: PainKey; title: string; body: string[] }[] = [
  {
    key: "overwhelm",
    title: "You look at a mastopexy case and aren't sure which variables deserve your attention first.",
    body: [
      "Patient anatomy, breast characteristics, asymmetry, skin quality, goals and surgical considerations can quickly become overwhelming.",
    ],
  },
  {
    key: "time",
    title: "You spend too much time jumping between different resources.",
    body: [
      "One lecture explains one concept. Another explains a different technique. Your notes are somewhere else.",
      "You end up trying to reconstruct the complete sequence yourself.",
    ],
  },
  {
    key: "puzzle",
    title: "You know the techniques, but struggle to organize them into one coherent framework.",
    body: [
      "You understand individual concepts but want a better way to connect the preoperative evaluation, planning and operative sequence.",
    ],
  },
  {
    key: "passive",
    title: "You consume surgical education passively.",
    body: [
      "You watch another procedure. You read another article. You attend another conference.",
      "But you don't always get an opportunity to actively review your reasoning through cases.",
    ],
  },
  {
    key: "search",
    title: "Every mastopexy case seems slightly different.",
    body: [
      "Different anatomy. Different goals. Different degrees of ptosis. Different tissue characteristics. Different planning considerations.",
      "You want a framework that helps you organize those differences.",
    ],
  },
  {
    key: "brain",
    title: "You want to improve your preoperative thinking.",
    body: [
      "Not because you need someone to make the decision for you — but because you want a clearer process for reviewing the information in front of you.",
    ],
  },
];

export type ToolKey = "sheet" | "checklist" | "map" | "matrix" | "camera";

export const tools: { key: ToolKey; title: string; body: string }[] = [
  {
    key: "sheet",
    title: "The 15-Minute Mastopexy Review Sheet",
    body: "A structured worksheet designed to help you review an educational case from beginning to end.",
  },
  {
    key: "checklist",
    title: "The Preoperative Review Checklist",
    body: "A repeatable checklist for organizing the information you want to review before studying a case.",
  },
  {
    key: "map",
    title: "The Mastopexy Planning Map",
    body: "A visual framework for organizing the major case variables.",
  },
  {
    key: "matrix",
    title: "The Case Comparison Matrix",
    body: "Compare educational cases side by side and identify similarities, differences and learning points.",
  },
  {
    key: "camera",
    title: "The Visual Case Documentation Guide",
    body: "A structured educational guide for organizing clinical photography and visual case documentation.",
  },
];

export const beforeItems = [
  "You jump between different resources.",
  "Every mastopexy case feels like a completely different problem.",
  "Your case-review notes are inconsistent.",
  "You know individual techniques but don't always have a structured way to review the complete sequence.",
  "You finish studying a case without systematically extracting the lesson.",
];

export const afterItems = [
  "You have a repeatable framework for reviewing mastopexy cases.",
  "You can work through an educational case in approximately 15 minutes.",
  "You have standardized planning and review worksheets.",
  "You can compare different cases using the same framework.",
  "You can systematically document what you learned.",
  "You have a structured way to revisit difficult scenarios.",
  "You build your own professional case-learning archive.",
];

export const guaranteeItems = [
  "Open all six volumes",
  "Review the complete Blueprint",
  "Work through the educational cases",
  "Use the planning tools",
  "Test the checklists",
  "Explore the bonus materials",
];

export const testimonials = [
  {
    name: "Dr. Sarah Mitchell",
    role: "Plastic Surgeon · Austin, TX",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/86b9f5a8-983a-4734-a095-d50999bfa982.jpg",
    quote:
      "The biggest difference for me is the structure. Instead of jumping between different resources, I now have a consistent framework for reviewing each case.",
  },
  {
    name: "Dr. Robert Hayes",
    role: "Plastic Surgeon · Manchester, UK",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/49111b4e-57b0-420e-89c5-30de26710cbf.jpg",
    quote:
      "The case atlas changed the way I study. I now force myself to identify the important variables before looking at the educational review.",
  },
  {
    name: "Dr. Lucía Romero",
    role: "Aesthetic Surgeon · Miami, FL",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/cdf23ec1-cace-440b-9d66-82c3fc640ac1.jpg",
    quote:
      "It doesn't feel like another textbook. It feels like a tool I can actually use when reviewing cases.",
  },
  {
    name: "Dr. Kenji Tanaka",
    role: "Plastic Surgeon · Vancouver, CA",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/f007aad3-9ed1-44fd-a9ec-1cba7f937a87.jpg",
    quote: "The review sheets and checklists have made my study sessions much more organized.",
  },
  {
    name: "Dr. Marcus Bennett",
    role: "Plastic Surgeon · Atlanta, GA",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/a6903891-9534-4972-a414-dbe391c3198a.jpg",
    quote:
      "For $27, I expected a few PDFs. Instead, I received a complete educational framework that I can return to repeatedly.",
  },
];

export const pillars = ["Cases", "Frameworks", "Checklists", "Procedure sequencing", "Review", "Reflection"];

export const faqs: { q: string; a: string[] }[] = [
  {
    q: "How will I receive Mastopexy Surgical Blueprint™?",
    a: ["Immediately after your purchase, you'll receive access to the digital materials and downloadable resources included in the product."],
  },
  {
    q: "Who is Mastopexy Surgical Blueprint™ for?",
    a: ["It is designed for qualified plastic surgeons, aesthetic physicians and other appropriately trained medical professionals seeking structured educational material for mastopexy case review and surgical education."],
  },
  {
    q: "Is this a surgical technique course?",
    a: [
      "No. The Blueprint is a structured educational resource designed to help organize the study and review of mastopexy procedures and cases.",
      "It does not replace formal surgical training, supervised operative experience, mentorship, current evidence, institutional protocols or professional judgment.",
    ],
  },
  {
    q: "Does the Blueprint provide a universal step-by-step surgical protocol?",
    a: ["No. The material organizes procedural education into a structured sequence, but surgical decisions must always be individualized and made by appropriately trained professionals according to current evidence, applicable regulations and institutional protocols."],
  },
  {
    q: "How long does each case review take?",
    a: ["The core framework is designed so that an educational case can be reviewed in approximately 15 minutes, depending on the complexity of the case and the depth of review."],
  },
  {
    q: "Can I print the materials?",
    a: ["Yes. The worksheets, checklists, case-review tools and cards are designed to be downloaded and printed."],
  },
  {
    q: "Is this a one-time payment?",
    a: ["Yes. The launch offer is a one-time payment of $27 USD with no recurring subscription. The price is automatically converted to your local currency at checkout."],
  },
  {
    q: "How long will I have access?",
    a: ["You receive lifetime digital access to the materials included with your purchase, subject to the applicable purchase terms."],
  },
  {
    q: "What happens if I don't like it?",
    a: ["You have a 7-day guarantee. Review the material and decide whether it is useful for your professional education. If it isn't, contact us within the guarantee period according to the purchase terms."],
  },
];

export const recentBuyers = [
  { name: "Dra. Valentina Ruiz", place: "Bogotá, Colombia" },
  { name: "Dr. Alejandro Torres", place: "Ciudad de México, México" },
  { name: "Dra. Camila Fernández", place: "Buenos Aires, Argentina" },
  { name: "Dr. Mateo Herrera", place: "Madrid, España" },
  { name: "Dra. Isabella Morales", place: "Santiago, Chile" },
  { name: "Dr. Sebastián Castro", place: "Lima, Perú" },
  { name: "Dra. Lucía Navarro", place: "Montevideo, Uruguay" },
  { name: "Dr. Andrés Vargas", place: "Quito, Ecuador" },
  { name: "Dra. Gabriela Mendoza", place: "Caracas, Venezuela" },
  { name: "Dr. Diego Ramírez", place: "San José, Costa Rica" },
  { name: "Dra. Sofía Jiménez", place: "Panamá, Panamá" },
  { name: "Dr. Javier Ortega", place: "Santo Domingo, Rep. Dominicana" },
  { name: "Dra. Mariana López", place: "Guadalajara, México" },
  { name: "Dr. Nicolás Rojas", place: "Medellín, Colombia" },
];
